/**
 * IntroScreen.js
 * ===============
 * 4-slide introduction shown once per session after consent.
 *
 *  Slide 1 — Welcome to EYE2VOICE
 *  Slide 2 — How This App Works
 *  Slide 3 — Two Modes (Quick Chat / Conversation Mode)
 *  Slide 4 — Tips & Tricks
 *
 * Layout: cream header (heading only) + white card with curved top (body + illus + dots + buttons).
 * Skip (slides 1-3) or Next (slide 4) navigates to /landing.
 * Route: /intro
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './IntroScreen.css';

const TOTAL = 4;

// ── Blockie slide 1 — bounces + eyes look around ─────────────────────────────

function Blockie() {
  return (
    <div className="is-mascot-wrap is-mascot-wrap--bounce">
      <svg xmlns="http://www.w3.org/2000/svg" width="118" height="119" viewBox="0 0 118 119" fill="none" aria-hidden="true">
        <rect width="118" height="119" fill="#925AAD"/>
        <path d="M29 17C29 13.134 32.134 10 36 10V10C39.866 10 43 13.134 43 17V54C43 57.3137 40.3137 60 37 60H35C31.6863 60 29 57.3137 29 54V17Z" fill="white"/>
        <path d="M56 17C56 13.134 59.134 10 63 10V10C66.866 10 70 13.134 70 17V54C70 57.3137 67.3137 60 64 60H62C58.6863 60 56 57.3137 56 54V17Z" fill="white"/>
        <rect x="57" y="15" width="11" height="11" fill="#5E3971" className="is-pupil"/>
        <rect x="30" y="15" width="11" height="11" fill="#5E3971" className="is-pupil"/>
      </svg>
      <div className="is-mascot-shadow" />
    </div>
  );
}

// ── Blockie slide 2 — shadow built-in, eyes only (left/right/up/down) ─────────

function BlockieSlide2() {
  return (
    <div className="is-mascot-wrap">
      <svg xmlns="http://www.w3.org/2000/svg" width="167" height="210" viewBox="0 0 167 210" fill="none" aria-hidden="true">
        <rect x="27" width="118" height="119" fill="#925AAD"/>
        <path d="M56 17C56 13.134 59.134 10 63 10V10C66.866 10 70 13.134 70 17V54C70 57.3137 67.3137 60 64 60H62C58.6863 60 56 57.3137 56 54V17Z" fill="white"/>
        <path d="M83 17C83 13.134 86.134 10 90 10V10C93.866 10 97 13.134 97 17V54C97 57.3137 94.3137 60 91 60H89C85.6863 60 83 57.3137 83 54V17Z" fill="white"/>
        <rect x="89" y="10" width="11" height="11" fill="#5E3971" className="is-pupil-cardinal"/>
        <rect x="61" y="10" width="11" height="11" fill="#5E3971" className="is-pupil-cardinal"/>
        <g filter="url(#filter0_f_122_433)">
          <path d="M54 169.5L78.5 163L144.5 169.5L100.5 187.5L22 182L54 169.5Z" fill="#D9D9D9"/>
        </g>
        <defs>
          <filter id="filter0_f_122_433" x="0" y="141" width="166.5" height="68.5" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
            <feGaussianBlur stdDeviation="11" result="effect1_foregroundBlur_122_433"/>
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// ── Triangle (slide 3) — bounces, eyes static ────────────────────────────────

function TriangleSlide3() {
  return (
    <div className="is-mascot-wrap is-mascot-wrap--bounce">
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="110" viewBox="0 0 144 158" fill="none" aria-hidden="true">
        <path d="M57.6737 107.326C64.3624 117.759 79.6074 117.759 86.2962 107.326L117.544 58.5874C124.798 47.2727 116.673 32.4121 103.233 32.4121H40.7371C27.2967 32.4121 19.1717 47.2727 26.4259 58.5874L57.6737 107.326Z" fill="#63B3F0"/>
        <circle cx="73.8694" cy="54.2714" r="10.0528" fill="white" stroke="#F5F5F5"/>
        <circle cx="94.9749" cy="54.2714" r="10.0528" fill="white" stroke="#F5F5F5"/>
        <circle cx="77.6382" cy="47.4874" r="3.76884" fill="#2771A9"/>
        <circle cx="98.7437" cy="47.4874" r="3.76884" fill="#2771A9"/>
        <g filter="url(#filter_tri_shadow)">
          <ellipse cx="70.1005" cy="148.869" rx="18.0905" ry="1.13065" fill="#D9D9D9"/>
        </g>
        <defs>
          <filter id="filter_tri_shadow" x="44.01" y="139.739" width="52.1809" height="18.2613" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
            <feGaussianBlur stdDeviation="4" result="effect1_foregroundBlur_tri"/>
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// ── Blockie (slide 3) — smaller, bounces with delay, eyes static ─────────────

function BlockieSlide3() {
  return (
    <div className="is-mascot-wrap is-mascot-wrap--bounce is-mascot-wrap--delay">
      <svg xmlns="http://www.w3.org/2000/svg" width="85" height="98" viewBox="0 0 122 141" fill="none" aria-hidden="true">
        <rect x="28" width="74.576" height="75.208" fill="#925AAD"/>
        <path d="M43.4878 10.744C43.4878 8.3007 45.4685 6.32001 47.9118 6.32001C50.3551 6.32001 52.3358 8.3007 52.3358 10.744V33.496C52.3358 35.9393 50.3551 37.92 47.9118 37.92C45.4685 37.92 43.4878 35.9393 43.4878 33.496V10.744Z" fill="white"/>
        <path d="M60.552 10.744C60.552 8.3007 62.5327 6.32001 64.976 6.32001C67.4193 6.32001 69.4 8.3007 69.4 10.744V33.496C69.4 35.9393 67.4193 37.92 64.976 37.92C62.5327 37.92 60.552 35.9393 60.552 33.496V10.744Z" fill="white"/>
        <rect x="61" y="6" width="6.952" height="6.952" fill="#5E3971"/>
        <rect x="43" y="6" width="6.952" height="6.952" fill="#5E3971"/>
        <g filter="url(#filter_bl3_shadow)">
          <path d="M42.224 107.124L57.708 103.016L99.42 107.124L71.612 118.5L22 115.024L42.224 107.124Z" fill="#D9D9D9"/>
        </g>
        <defs>
          <filter id="filter_bl3_shadow" x="0" y="81.016" width="121.42" height="59.484" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
            <feGaussianBlur stdDeviation="11" result="effect1_foregroundBlur_bl3"/>
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// ── Triangle's pink circles — ascend from small (bottom-left) to large (top-right)

function ConversationBubbles() {
  return (
    <div className="is-conv-bubbles" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="55" height="62" viewBox="0 0 68 77" fill="none">
        <circle cx="6" cy="71" r="6" fill="#FFBDEC" fillOpacity="0.67" className="is-bubble-sm"/>
        <circle cx="19" cy="47" r="13" fill="#FFBDEC" fillOpacity="0.67" className="is-bubble-md"/>
        <circle cx="48" cy="20" r="20" fill="#FFBDEC" fillOpacity="0.67" className="is-bubble-lg"/>
      </svg>
    </div>
  );
}

// ── Blockie's teal squares — ascend from small (bottom-right) to large (top-left)

function BlockieBubbles() {
  return (
    <div className="is-conv-bubbles" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="68" height="67" viewBox="0 0 68 67" fill="none">
        <rect x="53" y="52" width="15" height="15" fill="#93E2CE" fillOpacity="0.85" className="is-bubble-sm"/>
        <rect x="38" y="26" width="21" height="21" fill="#93E2CE" fillOpacity="0.85" className="is-bubble-md"/>
        <rect x="0"  y="0"  width="26" height="26" fill="#93E2CE" fillOpacity="0.85" className="is-bubble-lg"/>
      </svg>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function IntroScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  function goNext() {
    if (step < TOTAL) {
      setStep(s => s + 1);
    } else {
      navigate('/landing', { replace: true });
    }
  }

  function goBack() {
    if (step > 1) setStep(s => s - 1);
  }

  function goSkip() {
    navigate('/landing', { replace: true });
  }

  // ── Swipe gesture ─────────────────────────────────────────────────────────
  const touchStartX = useRef(null);

  function onTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }

  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 50) {
      if (dx > 0) goNext();   // swipe left → next slide
      else        goBack();   // swipe right → previous slide
    }
    touchStartX.current = null;
  }

  const isLastSlide = step === TOTAL;

  return (
    <div className="is-screen" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

      {/* ── Cream header — heading only ─────────────────────────────────────── */}
      <div className="is-header">
        {step === 1 && (
          <h1 className="is-heading">
            Welcome to{' '}
            <span className="is-teal">EYE2VOICE</span>
          </h1>
        )}
        {step === 2 && <h2 className="is-heading">How This App Works:</h2>}
        {step === 3 && <h2 className="is-heading">We have made two modes:</h2>}
        {step === 4 && <h2 className="is-heading">Tips &amp; Tricks</h2>}
      </div>

      {/* ── White card — body, illustration, dots, buttons ─────────────────── */}
      <div className="is-card">

        {/* Body content */}
        <div className="is-body-area">
          {step === 1 && (
            <p className="is-body">
              This is a hands-free communication tool -navigated entirely with your eyes
            </p>
          )}
          {step === 2 && (
            <p className="is-body">
              This app doesn't track your eye position on the screen.
              Instead, it only detects the general eye movements: left,
              right, up, and down.
            </p>
          )}
          {step === 3 && (
            <div className="is-modes">
              <div className="is-mode">
                <span className="is-mode-num">1.</span>
                <div>
                  <p className="is-mode-name">Quick Chat</p>
                  <p className="is-mode-desc">
                    With pre-determined chat screens to guide conversation
                  </p>
                </div>
              </div>
              <div className="is-mode">
                <span className="is-mode-num">2.</span>
                <div>
                  <p className="is-mode-name">Conversation Mode</p>
                  <p className="is-mode-desc">
                    AI-powered responses by listening to conversation and
                    generating responses
                  </p>
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <ul className="is-tips">
              <li>+ If using the application on mobile, use in portrait mode</li>
              <li>+ If on a tablet, keep it at least 12 inches away from the user's face</li>
              <li>+ Mildly exaggerate directional looks, especially up and down</li>
            </ul>
          )}
        </div>

        {/* Illustration */}
        <div className={`is-illus${step === 3 ? ' is-illus--slide3' : ''}`}>
          {step === 1 && <Blockie />}
          {step === 2 && <BlockieSlide2 />}
          {step === 3 && (
            <>
              <div className="is-char-col">
                <ConversationBubbles />
                <TriangleSlide3 />
              </div>
              <div className="is-char-col">
                <BlockieBubbles />
                <BlockieSlide3 />
              </div>
            </>
          )}
          {step === 4 && <Blockie />}
        </div>

        {/* Progress dots */}
        <div className="is-dots" role="status" aria-label={`Step ${step} of ${TOTAL}`}>
          {Array.from({ length: TOTAL }, (_, i) => (
            <div
              key={i}
              className={`is-dot${i + 1 === step ? ' is-dot--active' : ''}`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className={`is-actions${isLastSlide ? ' is-actions--end' : ''}`}>
          {!isLastSlide && (
            <button className="is-btn is-btn--skip" onClick={goSkip}>
              Skip
            </button>
          )}
          <button className="is-btn is-btn--next" onClick={goNext}>
            Next
          </button>
        </div>

      </div>
    </div>
  );
}
